import React, { useEffect, useState } from "react";
import { ScrollText, Plus, Pencil, Trash2, Filter } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/Cards";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import SearchBox, { matches } from "@/components/SearchBox";

const ICON = { POST: Plus, PUT: Pencil, DELETE: Trash2 };
const COLOR = { POST: "text-emerald-400 bg-emerald-400/10", PUT: "text-sky-400 bg-sky-400/10", DELETE: "text-rose-400 bg-rose-400/10" };
const KEY_TR = { name: "Ad", amount: "Tutar", description: "Açıklama", category: "Kategori", date: "Tarih", month: "Ay", headcount: "Kişi", email: "E-posta", type: "Tür", unit_price: "Fiyat", meal_type: "Öğün", entries: "Satır" };

export default function AuditLog() {
  const [data, setData] = useState({ items: [], entities: [] });
  const [users, setUsers] = useState([]);
  const [f, setF] = useState({ user_email: "ALL", entity: "ALL", date_from: "", date_to: "" });
  const [search, setSearch] = useState("");
  const detailOf = (a) => Object.entries(a.detail || {}).map(([k, v]) => `${KEY_TR[k] || k}: ${typeof v === "number" && k === "amount" ? v.toLocaleString("tr-TR") + " ₺" : v}`).join(" · ");
  const shown = data.items.filter((a) => matches(search, a.entity, a.action, a.user_email, a.entity_id, detailOf(a)));

  const load = () => {
    const params = {};
    if (f.user_email !== "ALL") params.user_email = f.user_email;
    if (f.entity !== "ALL") params.entity = f.entity;
    if (f.date_from) params.date_from = f.date_from;
    if (f.date_to) params.date_to = f.date_to;
    api.get("/audit-log", { params }).then((r) => setData(r.data));
  };
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [f]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">İşlem Günlüğü</h1>
        <p className="text-sm text-muted-foreground mt-1">Kim, ne zaman, hangi kaydı ekledi / güncelledi / sildi. Sadece yönetici görebilir.</p>
      </div>

      <Card className="p-4 flex flex-col lg:flex-row gap-3 lg:items-center">
        <div className="flex items-center gap-2 text-sm font-medium"><Filter className="h-4 w-4 text-primary" /> Filtre</div>
        <Select value={f.user_email} onValueChange={(v) => setF({ ...f, user_email: v })}>
          <SelectTrigger className="h-10 w-full lg:w-[240px]" data-testid="audit-filter-user"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="ALL">Tüm kullanıcılar</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.email}>{u.name || u.email}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.entity} onValueChange={(v) => setF({ ...f, entity: v })}>
          <SelectTrigger className="h-10 w-full lg:w-[220px]" data-testid="audit-filter-entity"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="ALL">Tüm işlem türleri</SelectItem>{data.entities.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
        </Select>
        <Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="h-10 w-[150px] font-mono" data-testid="audit-filter-from" />
        <Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="h-10 w-[150px] font-mono" data-testid="audit-filter-to" />
        <SearchBox value={search} onChange={setSearch} placeholder="Kullanıcı, işlem, detay ara…" testId="audit-search-input" className="w-full lg:w-[260px]" />
        <Button variant="outline" onClick={() => { setF({ user_email: "ALL", entity: "ALL", date_from: "", date_to: "" }); setSearch(""); }} className="h-10" data-testid="audit-filter-reset">Sıfırla</Button>
        <span className="text-xs text-muted-foreground lg:ml-auto" data-testid="audit-count">{shown.length}{shown.length !== data.items.length ? ` / ${data.items.length}` : ""} kayıt</span>
      </Card>

      <Card className="overflow-hidden" data-testid="audit-table">
        <div className="divide-y divide-border/60">
          {shown.length === 0 && <div className="p-10 text-center text-muted-foreground text-sm">Kayıt bulunamadı</div>}
          {shown.map((a) => {
            const Icon = ICON[a.method] || ScrollText;
            const detail = detailOf(a);
            return (
              <div key={a.id} data-testid={`audit-row-${a.id}`} className="flex items-start gap-3 px-4 py-3 hover:bg-accent/30 transition-colors">
                <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${COLOR[a.method] || "bg-muted"}`}><Icon className="h-4 w-4" /></div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm"><span className="font-semibold">{a.entity}</span> <span className="text-muted-foreground">— {a.action}</span>
                    {a.entity_id && <span className="ml-2 font-mono text-[10px] text-muted-foreground">#{a.entity_id.slice(-6)}</span>}</div>
                  {detail && <div className="text-xs text-muted-foreground mt-0.5 truncate">{detail}</div>}
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs font-medium">{a.user_email || "sistem"}</div>
                  <div className="font-mono text-[11px] text-muted-foreground">{new Date(a.created_at).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
